import { useCallback, useEffect, useState } from "react";
import { api, idr, idrShort } from "../../lib/api";
import { toast } from "sonner";
import { Scale, Plus, TrendingUp, TrendingDown, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import LoadingState from "../../components/ui/loading-state";
import { formatTanggal } from "../../lib/tanggal";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";

// `GET /finance/opname/history` mengurut created_at desc lalu memotong
// `to_list(min(limit, 500))` — halaman ini tidak mengirim `limit`, jadi
// default backend (100) yang berlaku (lihat accounts.py::opname_history).
const OPNAME_BATAS_SERVER = 100;

export default function FinanceCashOpname() {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ count: 0, count_selisih: 0, total_selisih: 0 });
  const [kasBank, setKasBank] = useState([]);
  const [filterAkun, setFilterAkun] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const canAck = ["finance_ceo", "admin", "superadmin"].includes(user?.role);

  const ack = async (id) => {
    if (!confirm("Acknowledge anomaly ini? Aksi tercatat di audit trail.")) return;
    try { const r = await api.post(`/finance/opname/${id}/acknowledge`, {}); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get(`/finance/opname/history${filterAkun ? `?akun_id=${filterAkun}` : ""}`);
      setHistory(r.data.items || []);
      setStats({ count: r.data.count, count_selisih: r.data.count_selisih, total_selisih: r.data.total_selisih });
    } catch (e) { setLoadError(e); toast.error("Gagal load"); }
    finally { setLoading(false); }
  }, [filterAkun]);
  useEffect(() => {
    load();
    api.get("/finance/accounts/kas-bank").then(r => setKasBank((r.data || []).filter(a => a.category === "kas" || a.category === "petty_cash")));
  }, [load]);

  // Urutan bawaan (created_at desc dari backend) tetap sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedHistory = sortRows(history, sortKey, sortDir, {
    tanggal: (h) => h.tanggal || "",
    akun: (h) => h.akun_name || "",
    saldo_sistem: (h) => h.saldo_sistem || 0,
    jumlah_fisik: (h) => h.jumlah_fisik || 0,
    selisih: (h) => h.selisih || 0,
    oleh: (h) => h.created_by_name || "",
  });

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-cash-opname">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Cash Opname · Kas Fisik Toko</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Riwayat Opname</h1>
          <p className="text-sm text-slate-600 mt-1">Hitung fisik kas · sistem hitung selisih otomatis · keterangan wajib bila ada selisih</p>
        </div>
        <div className="flex gap-2">
          <select value={filterAkun} onChange={e => setFilterAkun(e.target.value)} data-testid="opn-filter-akun" className="h-10 px-3 border border-slate-200 rounded-lg text-sm">
            <option value="">Semua akun</option>
            {kasBank.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button onClick={() => nav("/finance/catat/cash_opname")} data-testid="opn-new" className="h-10 px-4 rounded-lg bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Opname Baru</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Opname</p>
          <p className="font-display text-2xl font-bold text-slate-900 mt-2">{stats.count}</p>
        </div>
        <div className={`border rounded-xl p-5 shadow-sm ${stats.count_selisih > 0 ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
          <p className={`text-[10px] font-semibold uppercase tracking-wider ${stats.count_selisih > 0 ? "text-amber-700" : "text-slate-500"}`}>Dengan Selisih</p>
          <p className={`font-display text-2xl font-bold mt-2 ${stats.count_selisih > 0 ? "text-amber-800" : "text-slate-900"}`}>{stats.count_selisih}</p>
        </div>
        <div className={`border rounded-xl p-5 shadow-sm ${Math.abs(stats.total_selisih) > 100 ? "bg-rose-50 border-rose-200" : "bg-white border-slate-200"}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Selisih Kumulatif</p>
          <p className={`font-display text-2xl font-bold mt-2 ${stats.total_selisih < 0 ? "text-rose-700" : stats.total_selisih > 0 ? "text-emerald-700" : "text-slate-900"}`}>{idr(stats.total_selisih)}</p>
        </div>
      </div>

      <BatasDaftarNotice shown={history.length} batas={OPNAME_BATAS_SERVER} />
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <LoadingState loading={loading} error={loadError} onRetry={load} varian="kartu" jumlahKartu={3} data-testid="loading-cash-opname">
        {history.length === 0 ? (
          <div className="p-10 text-center">
            <Scale className="w-12 h-12 mx-auto text-slate-400 mb-3" />
            <p className="font-display font-bold text-lg text-slate-900">Belum ada opname</p>
            <p className="text-sm text-slate-500 mt-1">Klik &quot;Opname Baru&quot; untuk mulai hitung kas fisik.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b text-[11px] uppercase text-slate-500"><tr>
                <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="opn-sort-tanggal" />
                <SortTh label="Akun" sortKeyName="akun" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="opn-sort-akun" />
                <SortTh label="Saldo Sistem" sortKeyName="saldo_sistem" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="opn-sort-saldo" />
                <SortTh label="Jumlah Fisik" sortKeyName="jumlah_fisik" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="opn-sort-fisik" />
                <SortTh label="Selisih" sortKeyName="selisih" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="opn-sort-selisih" />
                <th className="px-4 py-2.5 text-left">Keterangan</th>
                <SortTh label="Oleh" sortKeyName="oleh" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="opn-sort-oleh" />
                <th className="px-4 py-2.5 text-center">Anomaly</th>
              </tr></thead>
              <tbody>
                {sortedHistory.map(h => {
                  const punya = Math.abs(h.selisih) > 0.01;
                  return (
                    <tr key={h.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${h.is_anomaly ? "bg-rose-50/40" : punya ? "bg-amber-50/30" : ""}`}>
                      <td className="px-4 py-2.5 font-mono text-xs">{formatTanggal(h.tanggal)}</td>
                      <td className="px-4 py-2.5"><p className="font-medium">{h.akun_name}</p><p className="text-[10px] font-mono text-slate-400">{h.akun_code}</p></td>
                      <td className="px-4 py-2.5 text-right font-mono text-slate-700">{idr(h.saldo_sistem)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">{idr(h.jumlah_fisik)}</td>
                      <td className="px-4 py-2.5 text-right">
                        {punya ? (
                          <div className="text-right">
                            <span className={`inline-flex items-center gap-1 font-mono font-bold ${h.selisih < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                              {h.selisih < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
                              {h.selisih > 0 ? "+" : ""}{idr(h.selisih)}
                            </span>
                            {h.selisih_pct !== undefined && <p className="text-[10px] text-slate-500">{h.selisih_pct}%</p>}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-600 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Sesuai</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-600 max-w-xs truncate">{h.catatan || "-"}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{h.created_by_name}</td>
                      <td className="px-4 py-2.5 text-center">
                        {h.is_anomaly ? (
                          h.ceo_acknowledged ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"><ShieldCheck className="w-3 h-3" /> Acked</span>
                          ) : canAck ? (
                            <button onClick={() => ack(h.id)} data-testid={`opn-ack-${h.id}`} className="px-2 py-1 rounded-md bg-rose-600 text-white hover:bg-rose-700 text-[10px] font-bold inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> ACK</button>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200"><AlertTriangle className="w-3 h-3" /> Menunggu CEO</span>
                          )
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </LoadingState>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <b>Aturan Selisih:</b> Bila jumlah fisik ≠ saldo sistem, keterangan <b>wajib diisi</b> (kenapa selisih, kemana uangnya). Riwayat opname disimpan permanen untuk audit trail.
        </div>
      </div>
    </div>
  );
}
